/**
 * Trip linking audit and repair service.
 * Validates accommodation↔day bidirectional content blocks, normalizes legacy shapes,
 * backfills the connection registry, and promotes the trip to registry_primary reads.
 */
import { getDays, getAccommodation, updateAccommodation, updateDay } from '../firebase/firestoreService';
import { LINK_TYPES } from '../config/entityTypeRegistry';
import { createDayReferenceBlock } from '../utils/contentBlockUtils';
import {
  isAccommodationDayItineraryBlock,
  isLegacyAccommodationDayBlock
} from '../utils/linkExtractorUtils';
import { linkExtractorService } from './linkExtractorService';
import { connectionRegistryService } from './connectionRegistryService';
import { linkRegistryConfigService } from './linkRegistryConfigService';
import { formatDayDisplayLabel } from '../utils/dayDisplayUtils';
import { shouldRouteThroughRegistry } from '../utils/linkRegistryModeUtils';

function getDayDate(day) {
  return day?.date || day?.details?.date || null;
}

function getDayContentBlocks(day) {
  return Array.isArray(day?.content) ? day.content : [];
}

function formatDayName(day) {
  return formatDayDisplayLabel(day);
}

function isAccommodationDayReverseBlock(block) {
  if (!block || block.type === 'document_reference') {
    return false;
  }
  return isAccommodationDayItineraryBlock(block);
}

function findReverseBlock(accommodation, dayId, dayBlockId) {
  const blocks = accommodation.contentBlocks || [];
  return blocks.find((block) => {
    if (!isAccommodationDayReverseBlock(block) || block.dayId !== dayId) {
      return false;
    }
    if (dayBlockId && block.contentBlockId) {
      return block.contentBlockId === dayBlockId;
    }
    return true;
  }) || null;
}

function buildTypedDayReference(day, dayBlockId, context) {
  const dayId = day.id;
  const date = getDayDate(day);
  return createDayReferenceBlock(dayId, formatDayName(day), date, {
    contentBlockId: dayBlockId,
    context: context || null
  });
}

function normalizeReverseBlock(block, day, dayBlockId, context) {
  if (!block) {
    return buildTypedDayReference(day, dayBlockId, context);
  }

  if (block.type === 'day_reference') {
    const needsContentBlockId = dayBlockId && block.contentBlockId !== dayBlockId;
    const needsContext = context && !block.context;
    if (needsContentBlockId || needsContext) {
      return {
        ...block,
        contentBlockId: block.contentBlockId || dayBlockId,
        context: block.context || context || null,
        updatedAt: new Date().toISOString()
      };
    }
    return null;
  }

  if (isLegacyAccommodationDayBlock(block)) {
    return buildTypedDayReference(day, block.contentBlockId || dayBlockId, block.context || context);
  }

  return null;
}

function replaceOrAppendBlock(blocks, oldBlock, newBlock) {
  if (oldBlock) {
    return blocks.map((block) => (block === oldBlock ? newBlock : block));
  }
  return [...blocks, newBlock];
}

export const tripLinkAuditService = {
  async auditAccommodationDayLinks(userId, tripId, options = {}) {
    const { includeRegistryCoveredDrift = false } = options;
    const [days, accommodations] = await Promise.all([
      getDays(userId, tripId),
      getAccommodation(userId, tripId)
    ]);

    const accommodationMap = new Map(accommodations.map((item) => [item.id, item]));
    const issues = [];
    const pairs = [];

    days.forEach((day) => {
      getDayContentBlocks(day).forEach((block) => {
        if (block.type !== 'accommodation_reference' || !block.accommodationId) {
          return;
        }

        const accommodation = accommodationMap.get(block.accommodationId);
        const pair = {
          dayId: day.id,
          dayDate: getDayDate(day),
          dayBlockId: block.id,
          accommodationId: block.accommodationId,
          accommodationName: accommodation?.details?.name || block.accommodationName || 'Accommodation',
          context: block.context || null
        };
        pairs.push(pair);

        if (!accommodation) {
          issues.push({
            type: 'orphan_day_ref',
            severity: 'error',
            ...pair,
            message: 'Day references an accommodation that does not exist'
          });
          return;
        }

        const reverse = findReverseBlock(accommodation, day.id, block.id);
        if (!reverse) {
          issues.push({
            type: 'missing_reverse',
            severity: 'error',
            ...pair,
            message: 'Missing accommodation→day content block'
          });
          return;
        }

        if (reverse.type !== 'day_reference') {
          issues.push({
            type: 'untyped_reverse',
            severity: 'warn',
            ...pair,
            message: 'Accommodation→day block is legacy untyped shape'
          });
        }

        if (reverse.contentBlockId && block.id && reverse.contentBlockId !== block.id) {
          issues.push({
            type: 'content_block_id_mismatch',
            severity: 'warn',
            ...pair,
            reverseContentBlockId: reverse.contentBlockId,
            message: 'contentBlockId does not match day-side accommodation block id'
          });
        }
      });
    });

    accommodations.forEach((accommodation) => {
      (accommodation.contentBlocks || []).forEach((block) => {
        if (!isAccommodationDayReverseBlock(block)) {
          return;
        }

        const day = days.find((item) => item.id === block.dayId);
        if (!day) {
          issues.push({
            type: 'orphan_accommodation_ref',
            severity: 'error',
            accommodationId: accommodation.id,
            dayId: block.dayId,
            blockId: block.id,
            message: 'Accommodation references a day that does not exist'
          });
          return;
        }

        const forward = getDayContentBlocks(day).find((dayBlock) => (
          dayBlock.type === 'accommodation_reference'
          && dayBlock.accommodationId === accommodation.id
          && (!block.contentBlockId || dayBlock.id === block.contentBlockId)
        ));

        if (!forward) {
          issues.push({
            type: 'missing_forward',
            severity: 'error',
            accommodationId: accommodation.id,
            dayId: block.dayId,
            blockId: block.id,
            message: 'Accommodation→day block has no matching day→accommodation block'
          });
        }
      });
    });

    const registryConfig = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
    const filteredIssues = [];

    for (const issue of issues) {
      if (
        registryConfig.readMode === 'registry_only'
        && (issue.type === 'missing_forward' || issue.type === 'missing_reverse')
        && issue.accommodationId
        && issue.dayId
      ) {
        const registryCoversPair = await connectionRegistryService.linkExists(
          userId,
          tripId,
          { type: 'accommodation', id: issue.accommodationId },
          { type: 'day', id: issue.dayId },
          LINK_TYPES.STAYS_ON
        );
        if (registryCoversPair && !includeRegistryCoveredDrift) {
          continue;
        }
      }
      filteredIssues.push(issue);
    }

    const errorCount = filteredIssues.filter((issue) => issue.severity === 'error').length;
    const warnCount = filteredIssues.filter((issue) => issue.severity === 'warn').length;

    return {
      pairCount: pairs.length,
      issueCount: filteredIssues.length,
      errorCount,
      warnCount,
      issues: filteredIssues,
      pairs
    };
  },

  /**
   * Removes one-sided legacy accommodation↔day blocks when registry_only and stays_on exists in registry.
   * Fixes drift after incomplete legacy shadow cleanup.
   */
  async repairRegistryOnlyAccommodationDayDrift(userId, tripId, { dryRun = true } = {}) {
    const registryConfig = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
    // Pre-flip cleanup on registry_primary trips: strip orphan legacy blocks when registry has stays_on.
    if (registryConfig.readMode !== 'registry_only' && registryConfig.readMode !== 'registry_primary') {
      return { skipped: true, reason: 'not registry_primary or registry_only', actionCount: 0, actions: [] };
    }

    const audit = await this.auditAccommodationDayLinks(userId, tripId, {
      includeRegistryCoveredDrift: true
    });
    const driftIssues = (audit.issues || []).filter(
      (issue) => issue.type === 'missing_forward' || issue.type === 'missing_reverse'
    );

    if (driftIssues.length === 0) {
      return { dryRun, actionCount: 0, actions: [], accommodationsUpdated: 0, daysUpdated: 0 };
    }

    const [days, accommodations] = await Promise.all([
      getDays(userId, tripId),
      getAccommodation(userId, tripId)
    ]);
    const accommodationMap = new Map(accommodations.map((item) => [item.id, item]));
    const dayMap = new Map(days.map((item) => [item.id, item]));
    const accPending = new Map();
    const dayPending = new Map();
    const actions = [];

    const getAccBlocks = (accommodationId) => {
      if (accPending.has(accommodationId)) {
        return accPending.get(accommodationId);
      }
      return [...(accommodationMap.get(accommodationId)?.contentBlocks || [])];
    };

    const getDayContent = (dayId) => {
      if (dayPending.has(dayId)) {
        return dayPending.get(dayId);
      }
      const day = dayMap.get(dayId);
      return day ? [...getDayContentBlocks(day)] : [];
    };

    for (const issue of driftIssues) {
      const registryCoversPair = await connectionRegistryService.linkExists(
        userId,
        tripId,
        { type: 'accommodation', id: issue.accommodationId },
        { type: 'day', id: issue.dayId },
        LINK_TYPES.STAYS_ON
      );
      if (!registryCoversPair) {
        continue;
      }

      if (issue.type === 'missing_forward') {
        const nextBlocks = getAccBlocks(issue.accommodationId).filter(
          (block) => !(isAccommodationDayItineraryBlock(block) && block.dayId === issue.dayId)
        );
        accPending.set(issue.accommodationId, nextBlocks);
        actions.push({
          action: 'remove_orphan_accommodation_day_ref',
          accommodationId: issue.accommodationId,
          dayId: issue.dayId
        });
      }

      if (issue.type === 'missing_reverse') {
        const nextContent = getDayContent(issue.dayId).filter(
          (block) => !(
            block.type === 'accommodation_reference'
            && block.accommodationId === issue.accommodationId
          )
        );
        dayPending.set(issue.dayId, nextContent);
        actions.push({
          action: 'remove_orphan_day_accommodation_ref',
          accommodationId: issue.accommodationId,
          dayId: issue.dayId
        });
      }
    }

    const applied = { accommodations: [], days: [] };
    if (!dryRun) {
      for (const [accommodationId, contentBlocks] of accPending.entries()) {
        await updateAccommodation(userId, tripId, accommodationId, {
          contentBlocks,
          contentBlocksLastUpdated: new Date().toISOString()
        });
        applied.accommodations.push(accommodationId);
      }
      for (const [dayId, content] of dayPending.entries()) {
        await updateDay(userId, tripId, dayId, {
          content,
          updatedAt: new Date().toISOString()
        });
        applied.days.push(dayId);
      }
    }

    return {
      dryRun,
      actionCount: actions.length,
      actions,
      accommodationsUpdated: applied.accommodations.length,
      daysUpdated: applied.days.length,
      applied
    };
  },

  async fixAccommodationDayLinkIssues(userId, tripId, { dryRun = false } = {}) {
    const driftRepair = await this.repairRegistryOnlyAccommodationDayDrift(userId, tripId, { dryRun });
    const blockRepair = await this.fixAccommodationDayLinks(userId, tripId, { dryRun });
    return {
      driftRepair,
      blockRepair,
      actionCount: (driftRepair.actionCount || 0) + (blockRepair.actionCount || 0)
    };
  },

  async fixAccommodationDayLinks(userId, tripId, { dryRun = true } = {}) {
    const [days, accommodations] = await Promise.all([
      getDays(userId, tripId),
      getAccommodation(userId, tripId)
    ]);

    const accommodationMap = new Map(accommodations.map((item) => [item.id, item]));
    const pendingUpdates = new Map();
    const actions = [];

    const queueUpdate = (accommodationId, nextBlocks) => {
      pendingUpdates.set(accommodationId, nextBlocks);
    };

    const getWorkingBlocks = (accommodationId) => {
      if (pendingUpdates.has(accommodationId)) {
        return pendingUpdates.get(accommodationId);
      }
      const accommodation = accommodationMap.get(accommodationId);
      return [...(accommodation?.contentBlocks || [])];
    };

    days.forEach((day) => {
      getDayContentBlocks(day).forEach((block) => {
        if (block.type !== 'accommodation_reference' || !block.accommodationId) {
          return;
        }

        const accommodation = accommodationMap.get(block.accommodationId);
        if (!accommodation) {
          return;
        }

        const currentBlocks = getWorkingBlocks(accommodation.id);
        const reverse = findReverseBlock({ contentBlocks: currentBlocks }, day.id, block.id);

        if (!reverse) {
          const newBlock = buildTypedDayReference(day, block.id, block.context);
          queueUpdate(accommodation.id, [...currentBlocks, newBlock]);
          actions.push({
            action: 'add_reverse',
            accommodationId: accommodation.id,
            dayId: day.id,
            dayBlockId: block.id
          });
          return;
        }

        const normalized = normalizeReverseBlock(reverse, day, block.id, block.context);
        if (normalized) {
          queueUpdate(
            accommodation.id,
            replaceOrAppendBlock(currentBlocks, reverse, normalized)
          );
          actions.push({
            action: reverse.type === 'day_reference' ? 'patch_reverse' : 'normalize_reverse',
            accommodationId: accommodation.id,
            dayId: day.id,
            dayBlockId: block.id
          });
        }
      });
    });

    const applied = [];
    if (!dryRun) {
      for (const [accommodationId, contentBlocks] of pendingUpdates.entries()) {
        await updateAccommodation(userId, tripId, accommodationId, {
          contentBlocks,
          contentBlocksLastUpdated: new Date().toISOString()
        });
        applied.push({ accommodationId, blockCount: contentBlocks.length });
      }
    }

    return {
      dryRun,
      actionCount: actions.length,
      actions,
      accommodationsUpdated: dryRun ? pendingUpdates.size : applied.length,
      applied
    };
  },

  async auditTrip(userId, tripId) {
    const registryConfig = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
    const accommodationDayAudit = await this.auditAccommodationDayLinks(userId, tripId);

    let parity = null;
    if (shouldRouteThroughRegistry(registryConfig)) {
      parity = await connectionRegistryService.compareRegistryToLegacy(userId, tripId);
    }

    const passed = accommodationDayAudit.errorCount === 0
      && accommodationDayAudit.warnCount === 0
      && (!parity || (
        parity.legacyCandidates === 0
        && (parity.legacyShadows ?? 0) === 0
        && parity.unmapped === 0
      ));

    return {
      tripId,
      userId,
      registryConfig,
      accommodationDayAudit,
      parity,
      passed
    };
  },

  toHealthCheckIssues(auditReport) {
    if (!auditReport) {
      return [];
    }

    const issues = [];
    const accommodationDayAudit = auditReport.accommodationDayAudit || {};

    (accommodationDayAudit.issues || []).forEach((issue, index) => {
      const dayLabel = issue.dayDate || issue.dayId || 'unknown day';
      const accommodationLabel = issue.accommodationName || issue.accommodationId || 'accommodation';
      let description = issue.message;
      if (issue.type === 'missing_forward') {
        description = `Accommodation "${accommodationLabel}" has a day link for ${dayLabel}, but that day has no matching accommodation entry.`;
      } else if (issue.type === 'missing_reverse') {
        description = `Day ${dayLabel} lists "${accommodationLabel}", but that accommodation has no matching day link.`;
      }
      const autoFixable = ['missing_reverse', 'missing_forward', 'untyped_reverse'].includes(issue.type);
      issues.push({
        id: `link-audit-${issue.type}-${issue.dayId || ''}-${issue.accommodationId || index}`,
        title: issue.type === 'missing_reverse'
          ? 'Missing Accommodation-Day Link'
          : issue.type === 'missing_forward'
            ? 'Missing Day-Accommodation Link'
            : 'Accommodation-Day Link Issue',
        description,
        type: 'registry-link-audit',
        severity: issue.severity === 'error' ? 'high' : 'medium',
        entityType: issue.accommodationId ? 'accommodation' : 'day',
        entityId: issue.accommodationId || issue.dayId || null,
        canAutoFix: autoFixable,
        details: issue
      });
    });

    const parity = auditReport.parity;
    const missingCount = parity?.legacyCandidates ?? 0;
    const shadowCount = parity?.legacyShadows ?? 0;
    if (missingCount > 0 || shadowCount > 0) {
      const descriptionParts = [];
      if (missingCount > 0) {
        descriptionParts.push(
          `${missingCount} legacy link(s) need a registry entry`
        );
      }
      if (shadowCount > 0) {
        descriptionParts.push(
          `${shadowCount} duplicate legacy copy(ies) can be removed (registry already has these links)`
        );
      }
      issues.push({
        id: 'registry-parity-reconcile',
        title: 'Connection Registry Parity',
        description: `${descriptionParts.join('; ')}.`,
        type: 'registry-parity',
        severity: missingCount > 0 ? 'high' : 'medium',
        canAutoFix: true,
        details: parity
      });
    }

    if (parity?.unmapped > 0) {
      issues.push({
        id: 'registry-parity-unmapped',
        title: 'Unmapped Legacy Link Candidates',
        description: `${parity.unmapped} legacy link candidate(s) could not be mapped to registry entity types.`,
        type: 'registry-parity',
        severity: 'medium',
        canAutoFix: false,
        details: parity
      });
    }

    return issues;
  },

  async auditAndFixTrip(userId, tripId, {
    applyFixes = true,
    writeMode = 'dual_write',
    readMode = 'registry_primary'
  } = {}) {
    const beforeAudit = await this.auditAccommodationDayLinks(userId, tripId);
    const registryConfigBefore = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
    const parityBefore = await connectionRegistryService.compareRegistryToLegacy(userId, tripId);

    let fixResult = null;
    let extractResult = null;
    let reconcileResult = null;
    let registryConfigAfter = registryConfigBefore;
    let parityAfter = parityBefore;

    if (applyFixes) {
      fixResult = await this.fixAccommodationDayLinkIssues(userId, tripId, { dryRun: false });
      reconcileResult = await connectionRegistryService.reconcileLegacyParity(userId, tripId, {
        dryRun: false
      });
      extractResult = reconcileResult;
      registryConfigAfter = await linkRegistryConfigService.setLinkRegistryConfig(userId, tripId, {
        writeMode,
        readMode
      });
      parityAfter = await connectionRegistryService.compareRegistryToLegacy(userId, tripId);
    }

    const afterAudit = applyFixes
      ? await this.auditAccommodationDayLinks(userId, tripId)
      : beforeAudit;

    const passed = afterAudit.errorCount === 0
      && afterAudit.warnCount === 0
      && parityAfter.legacyCandidates === 0
      && (parityAfter.legacyShadows ?? 0) === 0
      && parityAfter.unmapped === 0
      && (reconcileResult?.errors?.length ?? 0) === 0;

    return {
      tripId,
      userId,
      applyFixes,
      passed,
      beforeAudit,
      afterAudit,
      fixResult,
      extractResult,
      reconcileResult,
      registryConfigBefore,
      registryConfigAfter,
      parityBefore,
      parityAfter
    };
  }
};

export default tripLinkAuditService;
